/**
 * The words the Tasks page prints, in one place (`docs/13-tasks-section-proposal.md` §2, §5).
 *
 * The Tasks section does **not** exist in monday, so none of these strings is a measured
 * capture: every one comes from the proposal the user approved on 2026-09-15, and where the
 * proposal is silent the label is marked **ASSUMED** in `tasks/T-T2.md`. The server keeps the
 * same vocabulary in `apps/api/src/services/tasks/vocabulary.ts`; the two must agree, which is
 * why the bucket order and the tab order are written down rather than derived from an object's
 * key order.
 */
import type {
  TaskArchiveDelay,
  TaskBucket,
  TaskDigestFrequency,
  TaskDoneFilter,
  TaskGroupBy,
  TaskPageTab,
  TaskSort,
} from './types';

/** The five page tabs, in the order the header renders them (`docs/13` §2 + §3 + §4). */
export const TASK_PAGE_TABS: readonly TaskPageTab[] = [
  'mine',
  'assigned',
  'archive',
  'insights',
  'timeline',
] as const;

export const TASK_TAB_LABELS: Record<TaskPageTab, string> = {
  mine: 'My Tasks',
  assigned: 'Tasks Assigned',
  archive: 'Archive',
  insights: 'Insights',
  timeline: 'Timeline',
};

/** The URL segment each tab owns. `mine` is the bare `/tasks`. */
export const TASK_TAB_PATHS: Record<TaskPageTab, string> = {
  mine: '/tasks',
  assigned: '/tasks/assigned',
  archive: '/tasks/archive',
  insights: '/tasks/insights',
  timeline: '/tasks/timeline',
};

export function tabFromPath(pathname: string): TaskPageTab {
  const tail = pathname.replace(/\/+$/, '').split('/')[2] ?? '';
  const found = TASK_PAGE_TABS.find((tab) => TASK_TAB_PATHS[tab].endsWith(`/${tail}`) && tail !== '');
  return found ?? 'mine';
}

/**
 * **P-319** · the segment the item card's URL adds — monday's own legacy `pulses`, the one every
 * migrated link carries (`routes/index.tsx` header, doc 00).
 */
export const TASK_ITEM_SEGMENT = 'pulses';

/**
 * The deep link for an item card opened **over** the Tasks page: `/tasks/pulses/<itemId>`, and
 * `/tasks/<tab>/pulses/<itemId>` on the other four tabs.
 *
 * The tab stays in the path because it is the list the card is open *over*: dropping it would
 * send somebody who opened a row from Tasks Assigned back to My Tasks when the card closes, which
 * is the very thing P-319 is about (the list state has to survive the card).
 */
export function taskItemUrl(tab: TaskPageTab, itemId: string): string {
  return `${TASK_TAB_PATHS[tab]}/${TASK_ITEM_SEGMENT}/${itemId}`;
}

/**
 * The item id out of such a path, or `null` — the one place that parses it, so the product route
 * and the `/__ui/tasks` harness read the same URL the same way.
 */
export function taskItemIdFromPath(pathname: string): string | null {
  const found = pathname.replace(/\/+$/, '').match(/\/pulses\/(\d+)$/);
  return found === null ? null : (found[1] ?? null);
}

/** Insights and Timeline take `tab: 'mine' | 'assigned'`; the list tabs answer for themselves. */
export function panelTabOf(tab: TaskPageTab, listTab: 'mine' | 'assigned'): 'mine' | 'assigned' {
  return tab === 'insights' || tab === 'timeline' ? listTab : tab === 'assigned' ? 'assigned' : 'mine';
}

export const TASK_BUCKET_LABELS: Record<TaskBucket, string> = {
  overdue: 'Overdue',
  today: 'Today',
  this_week: 'This week',
  later: 'Later',
  no_date: 'No date',
};

export const TASK_GROUP_BY_LABELS: Record<TaskGroupBy, string> = {
  due: 'Due date',
  board: 'Board',
  assigner: 'Assigner',
  assignee: 'Assignee',
  status: 'Status',
};

/**
 * Which groupings each tab offers (`docs/13` §2).
 *
 * *My Tasks* groups by due / board / assigner / status — grouping my own tasks by **me** says
 * nothing. *Tasks Assigned* groups by member first, which is what a delegation list is for.
 */
export const TASK_GROUP_BY_FOR_TAB: Record<'mine' | 'assigned' | 'archive', readonly TaskGroupBy[]> = {
  mine: ['due', 'board', 'assigner', 'status'],
  assigned: ['assignee', 'board', 'status', 'due'],
  archive: ['due', 'board', 'assigner', 'status'],
};

export const TASK_SORT_LABELS: Record<TaskSort, string> = {
  due_asc: 'Due date, soonest first',
  due_desc: 'Due date, latest first',
  assigned_desc: 'Assigned, newest first',
  assigned_asc: 'Assigned, oldest first',
  name_asc: 'Name, A–Z',
};

export const TASK_DONE_FILTER_LABELS: Record<TaskDoneFilter, string> = {
  any: 'Open and done',
  open: 'Open only',
  done: 'Done only',
};

/**
 * The due-date ranges the filter bar offers (`docs/13` §2, extended by **P-301** §1).
 *
 * `custom` is last on purpose: it is not a window of its own but the door to one, and the two
 * date fields it opens are what carry the window (`TaskFilterState.dueFrom` / `dueTo`). Either
 * side may be left empty — "everything before the 30th" and "everything from the 1st" are both
 * things people ask for, and a range that demanded both would make the common case two gestures.
 */
export const TASK_DUE_RANGES = [
  'any',
  'overdue',
  'today',
  'this_week',
  'next_7',
  'next_30',
  'custom',
] as const;
export type TaskDueRange = (typeof TASK_DUE_RANGES)[number];

export const TASK_DUE_RANGE_LABELS: Record<TaskDueRange, string> = {
  any: 'Any due date',
  overdue: 'Overdue',
  today: 'Today',
  this_week: 'This week',
  next_7: 'Next 7 days',
  next_30: 'Next 30 days',
  custom: 'Custom\u2026',
};

/**
 * **Assigned on** — the second range this round adds (P-301 §1).
 *
 * It looks **backwards** where the due ranges look forwards, which is the whole reason it is its
 * own vocabulary rather than a reused one: *Next 7 days* and *Last 7 days* are opposite windows,
 * and offering a single list of ranges against two different axes is how a filter bar starts
 * lying about what it filtered.
 */
export const TASK_ASSIGNED_RANGES = ['any', 'last_7', 'last_30', 'last_90', 'custom'] as const;
export type TaskAssignedRange = (typeof TASK_ASSIGNED_RANGES)[number];

export const TASK_ASSIGNED_RANGE_LABELS: Record<TaskAssignedRange, string> = {
  any: 'Any time',
  last_7: 'Last 7 days',
  last_30: 'Last 30 days',
  last_90: 'Last 90 days',
  custom: 'Custom\u2026',
};

export const TASK_ARCHIVE_DELAY_LABELS: Record<TaskArchiveDelay, string> = {
  1: 'After 1 day',
  3: 'After 3 days',
  7: 'After 7 days',
  14: 'After 14 days',
  30: 'After 30 days',
};

export const TASK_DIGEST_LABELS: Record<TaskDigestFrequency, string> = {
  off: 'Off',
  daily: 'Daily',
  weekly: 'Weekly, on Monday',
};

/** The default nudge wording; the dialog offers it pre-filled and editable (`docs/13` §2). */
export function defaultNudgeMessage(itemName: string): string {
  return `Just a reminder about "${itemName}" — could you take a look?`;
}
